import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { openCookieSettings } from "../cookies/consent";
import { APP_HOME_PATH } from "../lib/app-paths";
import { useAuth, useDeleteAccount, useGetProfile, useSignOut } from "../hooks";

export const SettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile, isLoading } = useGetProfile(user?.id);
  const { signOut, loading: signingOut } = useSignOut();
  const { deleteAccount, loading: deleting } = useDeleteAccount();
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const [deleted, setDeleted] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate(APP_HOME_PATH, { replace: true });
    } catch {
      setError(t("settings.signOutError"));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAccount();
      setDeleted(true);
    } catch {
      setError(t("settings.deleteError"));
    }
  };

  if (isLoading) {
    return <p className="pp-status">{t("common.loading")}</p>;
  }

  if (deleted) {
    return (
      <main className="pp-screen">
        <h1>{t("settings.deletedTitle")}</h1>
        <p className="pp-subtitle">{t("settings.deletedShort")}</p>
        <Link to={APP_HOME_PATH} className="pp-btn">
          {t("common.goHome")}
        </Link>
      </main>
    );
  }

  const initial = profile?.name ? profile.name.charAt(0).toUpperCase() : "U";

  return (
    <main className="pp-screen">
      <header className="pp-header">
        <Link to={APP_HOME_PATH} className="pp-back">
          {t("common.backHome")}
        </Link>
        <h1>{t("settings.title")}</h1>
      </header>

      <div className="pp-avatar-lg" aria-hidden>
        {initial}
      </div>

      <section className="pp-card">
        <div className="pp-info">
          <p className="pp-muted">{t("common.name")}</p>
          <strong>{profile?.name || t("common.noName")}</strong>
        </div>
        <div className="pp-info">
          <p className="pp-muted">{t("common.email")}</p>
          <strong>{user?.email || t("common.noEmail")}</strong>
        </div>
        <div className="pp-info pp-lang-row">
          <p className="pp-muted">{t("common.language")}</p>
          <LanguageSwitcher />
        </div>
        <div className="pp-info pp-lang-row">
          <p className="pp-muted">{t("cookies.manage")}</p>
          <button
            type="button"
            className="pp-text-btn"
            onClick={openCookieSettings}
          >
            {t("cookies.change")}
          </button>
        </div>
      </section>

      {error && <p className="pp-error">{error}</p>}

      <div className="pp-actions">
        {confirmSignOut ? (
          <div className="pp-confirm">
            <p>{t("settings.signOutConfirm")}</p>
            <button
              type="button"
              className="pp-btn"
              onClick={handleSignOut}
              disabled={signingOut || deleting}
            >
              {signingOut ? t("settings.signingOut") : t("settings.signOut")}
            </button>
            <button
              type="button"
              className="pp-btn-secondary"
              onClick={() => setConfirmSignOut(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="pp-btn-secondary"
            onClick={() => {
              setConfirmDelete(false);
              setConfirmSignOut(true);
            }}
            disabled={signingOut || deleting}
          >
            {t("settings.signOut")}
          </button>
        )}

        {confirmDelete ? (
          <div className="pp-confirm">
            <p>{t("settings.deleteConfirm")}</p>
            <button
              type="button"
              className="pp-btn-danger"
              onClick={handleDelete}
              disabled={signingOut || deleting}
            >
              {deleting ? t("settings.deleting") : t("settings.deleteAccount")}
            </button>
            <button
              type="button"
              className="pp-btn-secondary"
              onClick={() => setConfirmDelete(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="pp-btn-danger"
            onClick={() => {
              setConfirmSignOut(false);
              setConfirmDelete(true);
            }}
            disabled={signingOut || deleting}
          >
            {t("settings.deleteAccount")}
          </button>
        )}
      </div>
    </main>
  );
};
